import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatusBadge, fmtUSD, fmtDate } from "@/components/ui-bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/pages/Login";

export default function AdminWithdrawals() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [approving, setApproving] = useState(null);
  const [txnId, setTxnId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/withdrawals${filter ? `?status=${filter}` : ""}`);
    setRows(data);
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const reject = async (id) => {
    try { await api.post(`/admin/withdrawals/${id}/reject`); toast.success("Rejected"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmApprove = async () => {
    if (!txnId.trim()) return toast.error("Transaction ID is required");
    setBusy(true);
    try {
      await api.post(`/admin/withdrawals/${approving.id}/approve`, { transaction_id: txnId.trim() });
      toast.success("Approved & marked transferred");
      setApproving(null); setTxnId(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader title="Withdrawal Approvals"
        action={
          <div className="flex flex-wrap gap-2">
            {["pending", "approved", "rejected", ""].map((f) => (
              <button key={f || "all"} onClick={() => setFilter(f)} data-testid={`wd-filter-${f || "all"}`}
                      className={`px-2.5 sm:px-3 py-1.5 text-[10px] sm:text-xs uppercase tracking-wider rounded-lg border ${filter === f ? "bg-[#00F0FF] text-[#060B19] border-[#00F0FF]" : "bg-[#0A132A] border-white/10 text-[#94A3B8] hover:border-[#00F0FF]/40"}`}>
                {f || "All"}
              </button>
            ))}
          </div>
        }
      />
      <div className="glass p-5 overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="text-[11px] uppercase tracking-wider text-[#475569]">
            <tr className="border-b border-white/5">
              <th className="text-left py-2">User</th><th>Type</th><th className="text-right">Amount</th>
              <th className="text-left pl-4">Wallet</th><th className="text-left pl-4">Txn ID</th>
              <th className="text-right">Date</th><th className="text-right">Status</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="admin-withdrawals-table">
            {rows.map((w) => (
              <tr key={w.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="py-3">
                  <div className="font-semibold text-sm">{w.user?.name}</div>
                  <div className="text-xs text-[#00F0FF] font-mono">{w.user?.user_id}</div>
                </td>
                <td className="capitalize text-sm">{w.type}</td>
                <td className="text-right font-mono">{fmtUSD(w.amount)}</td>
                <td className="pl-4 font-mono text-xs text-[#94A3B8] truncate max-w-[140px]">{w.wallet_address || "—"}</td>
                <td className="pl-4 font-mono text-xs text-[#00F0FF] truncate max-w-[140px]">{w.transaction_id || "—"}</td>
                <td className="text-right text-[#94A3B8]">{fmtDate(w.created_at)}</td>
                <td className="text-right"><StatusBadge status={w.status} /></td>
                <td className="text-right">
                  {w.status === "pending" && (
                    <div className="inline-flex gap-2">
                      <button onClick={() => { setApproving(w); setTxnId(""); }} data-testid={`approve-wd-${w.id}`} className="px-2.5 py-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded text-xs hover:bg-emerald-500/25">Approve</button>
                      <button onClick={() => reject(w.id)} data-testid={`reject-wd-${w.id}`} className="px-2.5 py-1 bg-red-500/15 text-red-300 border border-red-500/30 rounded text-xs hover:bg-red-500/25">Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-[#475569]">No withdrawals</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!approving} onOpenChange={(o) => !o && setApproving(null)}>
        <DialogContent className="bg-[#0A132A] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display">Process Withdrawal</DialogTitle></DialogHeader>
          {approving && (
            <div className="space-y-4">
              <div className="text-sm text-[#94A3B8] space-y-1">
                <div>Amount: <span className="text-white font-mono">{fmtUSD(approving.amount)}</span></div>
                <div>Type: <span className="text-white capitalize">{approving.type}</span></div>
                <div>To wallet: <span className="font-mono break-all text-[#00F0FF]">{approving.wallet_address || "—"}</span></div>
              </div>
              <Field label="Transaction ID / Hash" value={txnId} onChange={setTxnId} testid="approve-txn-id" placeholder="0x..." />
              <button onClick={confirmApprove} disabled={busy} data-testid="confirm-approve-wd"
                className="w-full py-3 rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] transition-all disabled:opacity-50">
                {busy ? "Processing…" : "Confirm & Mark Transferred"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
